import type { ExperienceConfiguration, Reference } from "@ninetailed/experience.js-shared";
import type { VariantViewedInput } from "@/lib/analytics/segment";
import type { VariantData } from "./mapEntryExperiences";

function getVariantName<TFields>(variant: VariantData<TFields> | Reference): string {
  if ("fields" in variant) {
    const { internalTitle, name } = variant.fields as { internalTitle?: unknown; name?: unknown };
    if (typeof internalTitle === "string" && internalTitle.length > 0) return internalTitle;
    if (typeof name === "string" && name.length > 0) return name;
  }
  return variant.id;
}

/**
 * Maps the server-resolved exposure map (Record<experienceId, variantIndex>) to
 * the "Variant Viewed" events that should fire. Index `0` is the baseline arm and
 * emits an event describing the baseline; index `N` describes the variant at the
 * EntryReplacement component's `variants[index - 1]`. Experiences absent from the
 * map (no resolved arm) produce nothing.
 */
export function selectActiveVariants<TFields>(
  experiences: ExperienceConfiguration<VariantData<TFields>>[],
  exposures: Record<string, number>,
  baseline: VariantData<TFields>,
): VariantViewedInput[] {
  const result: VariantViewedInput[] = [];
  for (const experience of experiences) {
    const index = exposures[experience.id];
    if (index === undefined) continue;

    if (index === 0) {
      result.push({
        experienceId: experience.id,
        experienceName: experience.name,
        variantId: baseline.id,
        variantName: getVariantName(baseline),
        variantIndex: 0,
      });
      continue;
    }

    const replacement = experience.components.find(component => component.type === "EntryReplacement");
    const variant = replacement?.variants[index - 1];
    if (!variant) continue;

    result.push({
      experienceId: experience.id,
      experienceName: experience.name,
      variantId: variant.id,
      variantName: getVariantName(variant),
      variantIndex: index,
    });
  }
  return result;
}
